import React, { useState, useEffect } from "react";
import "../styles/GerenciamentoAlunos.css";
import supabase from "../config/supabaseConfig";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPen, faBan, faCheck } from "@fortawesome/free-solid-svg-icons";

// Iniciais para o avatar do aluno (ex.: "Marco Fernandes" -> "MF")
const iniciais = (nome = "") =>
  nome
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase() || "?";

const temRestricao = (lesao = "") => /sim|moderada|grave/i.test(lesao);

function GerenciamentoAlunos({ navigate }) {
  const [alunos, setAlunos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  // Página atual (27/07): a tabela cortava em `slice(0, entriesPerPage)` e não
  // havia como chegar no aluno 11 — com 651 cadastros, a recepção só enxergava
  // o começo da lista.
  const [pagina, setPagina] = useState(1);
  const [atualizandoAluno, setAtualizandoAluno] = useState(null);
  const [activeTab, setActiveTab] = useState("ativos"); // Estado para controlar qual aba está ativa
  // Contador de aulas realizadas por aluno (derivado no backend — regra §2 —
  // via RPC aulas_realizadas_por_aluno; evita o teto de 1000 linhas do getAll).
  const [aulasRealizadasPorAluno, setAulasRealizadasPorAluno] = useState(
    () => new Map()
  );
  // Próxima avaliação por aluno (Fase 3, Task B2) — via RPC
  // proxima_avaliacao_por_aluno; mesmo padrão do contador de aulas acima
  // (1 chamada, Map por aluno_id, evita N+1 nos 589 alunos).
  const [proximaAvaliacaoPorAluno, setProximaAvaliacaoPorAluno] = useState(
    () => new Map()
  );

  // Usar React Router diretamente quando navigate não é passado como prop
  const routerNavigate = useNavigate();

  // Função de navegação que usa a prop navigate ou o hook useNavigate
  const handleNavigate = (path) => {
    if (typeof navigate === "function") {
      navigate(path);
    } else {
      routerNavigate(path);
    }
  };

  // Forçar atualização do cache do esquema na montagem
  useEffect(() => {
    async function inicializar() {
      try {
        await carregarAlunos();
      } catch (err) {
        setError("Erro ao inicializar a página. Tente recarregar.");
        setLoading(false);
      }
    }

    inicializar();
  }, []);

  // Carregar o contador de aulas realizadas por aluno (agregado no backend).
  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const { data, error } = await supabase.rpc(
          "aulas_realizadas_por_aluno"
        );
        if (error) throw error;
        if (ativo) {
          setAulasRealizadasPorAluno(
            new Map((data || []).map((r) => [r.aluno_id, r.total]))
          );
        }
      } catch (err) {
        console.error("Erro ao carregar contador de aulas por aluno:", err);
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  // Duração do contrato por aluno (matrícula ativa → formato). Uma query só;
  // atenção ao teto de 1000 do PostgREST se a base passar de ~1000 matrículas
  // ativas (hoje 589) — nesse dia, virar RPC agregada como a de avaliação.
  const [duracaoPorAluno, setDuracaoPorAluno] = useState(() => new Map());
  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("matriculas")
          .select("aluno_id, formato")
          .eq("status", "ativa");
        if (error) throw error;
        if (ativo) {
          setDuracaoPorAluno(
            new Map((data || []).map((m) => [m.aluno_id, m.formato]))
          );
        }
      } catch (err) {
        console.error("Erro ao carregar duração das matrículas:", err);
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  // Carregar a próxima avaliação por aluno (agregado no backend, Task B2).
  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const { data, error } = await supabase.rpc(
          "proxima_avaliacao_por_aluno"
        );
        if (error) throw error;
        if (ativo) {
          setProximaAvaliacaoPorAluno(
            new Map((data || []).map((r) => [r.aluno_id, r.proxima]))
          );
        }
      } catch (err) {
        console.error("Erro ao carregar próxima avaliação por aluno:", err);
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  const carregarAlunos = async () => {
    try {
      setLoading(true);

      // Primeiro, vamos verificar se o usuário está autenticado
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        // Usuário não está autenticado
        setError("Usuário não está autenticado");
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("alunos")
        .select("*")
        .order("nome");

      if (error) {
        throw error;
      }

      setAlunos(data || []); // Garante que alunos seja sempre um array
      setError(null);
    } catch (err) {
      setError("Erro ao carregar alunos: " + err.message);
      setAlunos([]); // Define como array vazio em caso de erro
    } finally {
      setLoading(false);
    }
  };

  const atualizarStatusAluno = async (id, novoStatus) => {
    try {
      setAtualizandoAluno(id);
      setError(null);

      const { error } = await supabase
        .from("alunos")
        .update({ status: novoStatus })
        .eq("id", id);

      if (error) {
        throw error;
      }

      // Recarregar dados após atualização bem-sucedida
      await carregarAlunos();
    } catch (err) {
      setError(`Erro ao atualizar status: ${err.message}`);
    } finally {
      setAtualizandoAluno(null);
    }
  };

  const getLesaoClass = (lesao) => {
    switch (lesao) {
      case "Sim - Lesao Grave":
        return "lesao-grave";
      case "Sim - Lesao Moderada":
        return "lesao-moderada";
      default:
        return "";
    }
  };

  // Filtrar alunos por status (ativo/inativo) com base na aba selecionada
  const filtrarAlunosPorStatus = (alunos, status) => {
    return alunos.filter((aluno) => aluno.status === status);
  };

  // Filtrar alunos por termo de busca
  // Busca IGNORANDO ACENTO (fix 28/07). Antes só fazia toLowerCase, então
  // "Fatima" não achava "Fátima" — e quem não achava recadastrava. Foi assim
  // que nasceram 12 pares de aluno duplicado em prod (Jéssica/JESSICA,
  // Márcio/Marcio, Nádia/NADIA, Sônia/Sonia, Vitória/Vitoria...), cada um com
  // o histórico de aulas partido entre os dois cadastros.
  const normalizar = (s) =>
    (s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "");

  const filtrarAlunosPorBusca = (alunos, searchTerm) => {
    if (!searchTerm) return alunos;
    const termo = normalizar(searchTerm);

    return alunos.filter(
      (aluno) =>
        normalizar(aluno.nome).includes(termo) ||
        normalizar(aluno.telefone).includes(termo)
    );
  };

  // Aplicar ambos os filtros sequencialmente
  const alunosFiltrados = filtrarAlunosPorBusca(
    filtrarAlunosPorStatus(
      alunos,
      activeTab === "ativos" ? "ativo" : "inativo"
    ),
    searchTerm
  );

  // Paginação (27/07). `pagina` é clampada na renderização em vez de num
  // effect: mudar de aba, buscar ou trocar o "mostrar N" encolhe a lista e a
  // página corrente pode ficar além do fim — assim nunca cai numa tela vazia.
  const totalPaginas = Math.max(
    1,
    Math.ceil(alunosFiltrados.length / entriesPerPage)
  );
  const paginaAtual = Math.min(pagina, totalPaginas);
  const primeiroIndice = (paginaAtual - 1) * entriesPerPage;
  const alunosDaPagina = alunosFiltrados.slice(
    primeiroIndice,
    primeiroIndice + entriesPerPage
  );

  if (loading && !atualizandoAluno)
    return <div className="loading-indicator">Carregando...</div>;
  if (error) return <div className="erro">{error}</div>;

  return (
    <div className="alunos-principal-container">
      <h1>Gerenciamento de Alunos</h1>

      {/* Sistema de abas para alternar entre alunos ativos e inativos */}
      <div className="alunos-tabs">
        <button
          className={`tab-button ${activeTab === "ativos" ? "active" : ""}`}
          onClick={() => setActiveTab("ativos")}
          data-count={alunos.filter((aluno) => aluno.status === "ativo").length}
        >
          Alunos Ativos
        </button>
        <button
          className={`tab-button ${activeTab === "inativos" ? "active" : ""}`}
          onClick={() => setActiveTab("inativos")}
          data-count={
            alunos.filter((aluno) => aluno.status === "inativo").length
          }
        >
          Alunos Inativos
        </button>
      </div>

      <div className="actions-container">
        <button
          className="btn-cadastrar"
          onClick={() => handleNavigate("/cadastro-aluno")}
        >
          <span>+</span> Cadastrar Aluno
        </button>

        <div className="list-controls">
          <div className="show-entries">
            <span>Mostrar</span>
            <select
              value={entriesPerPage}
              onChange={(e) => setEntriesPerPage(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>registros</span>
          </div>

          <div className="search-box">
            <span>Buscar:</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Nome ou telefone"
            />
          </div>
        </div>
      </div>

      <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            <th>ALUNO</th>
            <th>PLANO</th>
            <th>DURAÇÃO</th>
            <th>NÍVEL</th>
            <th>RESTRIÇÃO</th>
            <th>PRÓX. AVALIAÇÃO</th>
            <th>AULAS</th>
            <th>AÇÕES</th>
          </tr>
        </thead>
        <tbody>
          {alunosDaPagina.map((aluno) => (
            <tr key={aluno.id} className={getLesaoClass(aluno.lesao)}>
              <td>
                <div className="aluno-cel">
                  <span className="aluno-av">{iniciais(aluno.nome)}</span>
                  <span>{aluno.nome}</span>
                </div>
              </td>
              <td>
                {aluno.plano ? (
                  <span className="badge-plano">{aluno.plano}</span>
                ) : (
                  "—"
                )}
              </td>
              <td>
                {(() => {
                  const formato = duracaoPorAluno.get(aluno.id);
                  const rotulo = {
                    mensal: "Mensal",
                    semestral: "Semestral",
                    anual: "Anual",
                  }[formato];
                  return <span className="prox-aval">{rotulo || "—"}</span>;
                })()}
              </td>
              <td>{aluno.nivel || "—"}</td>
              <td>
                {temRestricao(aluno.lesao) ? (
                  <span
                    className="restricao-cel"
                    title={aluno.tipo_lesao || aluno.lesao}
                  >
                    {aluno.tipo_lesao || "Sim"}
                  </span>
                ) : (
                  "—"
                )}
              </td>
              <td>
                {proximaAvaliacaoPorAluno.has(aluno.id) ? (
                  (() => {
                    // Destaque (14/07): vencida = vermelho; ≤7 dias = âmbar.
                    const dataISO = proximaAvaliacaoPorAluno.get(aluno.id);
                    const hoje = new Date().toISOString().slice(0, 10);
                    const em7 = new Date(Date.now() + 7 * 86400000)
                      .toISOString()
                      .slice(0, 10);
                    const urgencia =
                      dataISO < hoje
                        ? " prox-aval-vencida"
                        : dataISO <= em7
                        ? " prox-aval-proxima"
                        : "";
                    return (
                      <span className={`prox-aval${urgencia}`}>
                        {new Date(`${dataISO}T12:00:00`).toLocaleDateString(
                          "pt-BR"
                        )}
                      </span>
                    );
                  })()
                ) : (
                  "—"
                )}
              </td>
              <td>
                <span className="badge-aulas">
                  {aulasRealizadasPorAluno.get(aluno.id) || 0}
                </span>
              </td>
              <td className="acoes">
                <button
                  className="btn-acao btn-detalhes"
                  title="Ver detalhes"
                  onClick={() => {
                    try {
                      const path = `/detalhe-aluno/${aluno.id}`;
                      handleNavigate(path);
                    } catch (error) {
                      console.error(
                        "Erro ao navegar para detalhes do aluno:",
                        error
                      );
                      alert(
                        "Não foi possível abrir os detalhes do aluno. Por favor, tente novamente."
                      );
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faEye} />
                </button>
                <button
                  className="btn-acao btn-editar"
                  title="Editar"
                  onClick={() => {
                    try {
                      const path = `/editar-aluno/${aluno.id}`;
                      handleNavigate(path);
                    } catch (error) {
                      console.error(
                        "Erro ao navegar para edição do aluno:",
                        error
                      );
                      alert(
                        "Não foi possível abrir a edição do aluno. Por favor, tente novamente."
                      );
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faPen} />
                </button>
                {aluno.status === "ativo" ? (
                  <button
                    onClick={() => atualizarStatusAluno(aluno.id, "inativo")}
                    className="btn-acao btn-inativar"
                    title="Inativar"
                    disabled={atualizandoAluno === aluno.id}
                  >
                    {atualizandoAluno === aluno.id ? (
                      "..."
                    ) : (
                      <FontAwesomeIcon icon={faBan} />
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => atualizarStatusAluno(aluno.id, "ativo")}
                    className="btn-acao btn-ativar"
                    title="Ativar"
                    disabled={atualizandoAluno === aluno.id}
                  >
                    {atualizandoAluno === aluno.id ? (
                      "..."
                    ) : (
                      <FontAwesomeIcon icon={faCheck} />
                    )}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {alunosFiltrados.length > 0 && (
        <div className="paginacao">
          <span className="paginacao-info">
            {primeiroIndice + 1}–
            {Math.min(primeiroIndice + entriesPerPage, alunosFiltrados.length)}{" "}
            de {alunosFiltrados.length}
          </span>
          <div className="paginacao-botoes">
            <button
              type="button"
              onClick={() => setPagina(1)}
              disabled={paginaAtual === 1}
            >
              ««
            </button>
            <button
              type="button"
              onClick={() => setPagina(paginaAtual - 1)}
              disabled={paginaAtual === 1}
            >
              Anterior
            </button>
            <span className="paginacao-atual">
              {paginaAtual} / {totalPaginas}
            </span>
            <button
              type="button"
              onClick={() => setPagina(paginaAtual + 1)}
              disabled={paginaAtual === totalPaginas}
            >
              Próxima
            </button>
            <button
              type="button"
              onClick={() => setPagina(totalPaginas)}
              disabled={paginaAtual === totalPaginas}
            >
              »»
            </button>
          </div>
        </div>
      )}

      {alunosFiltrados.length === 0 && (
        <p className="sem-registros">
          {activeTab === "ativos"
            ? "Nenhum aluno ativo encontrado."
            : "Nenhum aluno inativo encontrado."}
        </p>
      )}

    </div>
  );
}

export default GerenciamentoAlunos;
